/**
 * Maparea tastelor grilei, ca functie PURA.
 *
 * E separata de starea cursorului pentru ca aici se greseste cel mai usor si aici se testeaza cel
 * mai ieftin: un tabel de intrari (tasta + modificatori + context) catre o actiune, fara DOM si
 * fara React.
 *
 * Doua reguli care explica majoritatea deciziilor de mai jos:
 *
 * 1. **Memoria musculara din Excel**. O cifra tastata pe o celula editabila incepe editarea cu
 *    acea cifra, exact ca in foaie. La fel `-` (sume negative), `,` si `.` (ambele inseamna
 *    separator zecimal, pentru ca tastatura numerica scrie `.` iar tastatura romaneasca `,`) si
 *    `=` (chiar tasteaza expresii: 55 de celule din foaia valutara si 69 din cea principala sunt
 *    sume de forma `=2515.67+2501.07`).
 * 2. **Literele sunt comenzi, nu text**. Pe o celula de suma, `A` inseamna "Aprobat", nu litera A.
 *    De aceea type-to-fill accepta doar caractere care pot incepe un numar.
 */

import { statusFromNumericAlias, statusFromShortCode, type StatusCode } from '@evomag/core/status';

export type ActiuneGrila =
  /** Deplasarea cursorului cu un pas relativ. `extinde` = Shift, adica selectie de interval. */
  | { tip: 'muta'; dRand: number; dCol: number; extinde: boolean }
  /** Salt absolut: `Home`, `End`, `Ctrl+Home`, `Ctrl+End`. */
  | { tip: 'muta-la'; rand: 'prima' | 'ultima' | 'aceeasi'; col: 'prima' | 'ultima' | 'aceeasi' }
  /** Un viewport in sus sau in jos. */
  | { tip: 'pagina'; directie: 1 | -1 }
  /** Incepe editarea. `caracter` e primul caracter tastat, sau `null` pentru F2 / dublu-clic. */
  | { tip: 'incepe-editare'; caracter: string | null }
  | { tip: 'commit'; apoi: 'jos' | 'sus' | 'dreapta' | 'stanga' | 'ramai' }
  | { tip: 'renunta' }
  | { tip: 'sterge' }
  | { tip: 'cicleaza-stare'; directie: 1 | -1 }
  | { tip: 'seteaza-stare'; cod: StatusCode }
  | { tip: 'comuta-selectie' }
  | { tip: 'selecteaza-tot' }
  | { tip: 'fill-down' }
  | { tip: 'copiaza' }
  | { tip: 'lipeste' }
  | { tip: 'deschide'; ce: 'facturi' | 'comentariu' | 'atasamente' | 'drawer' }
  | { tip: 'amana' }
  /** Navigare in timp, delegata ecranului: zile lucratoare, nu calendaristice. */
  | { tip: 'zi'; delta: number }
  | { tip: 'saptamana'; delta: number };

export interface ContextTasta {
  /** Suntem in mijlocul unei editari? Atunci majoritatea tastelor apartin inputului. */
  seEditeaza: boolean;
  /** Celula curenta accepta editare? */
  editabila: boolean;
  /** Celula curenta e numerica? Doar acolo are sens type-to-fill. */
  numerica: boolean;
}

/** Evenimentul de tastatura, redus la ce ne trebuie — ca testele sa nu construiasca DOM. */
export interface EvenimentTasta {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}

/** `Ctrl` pe Windows/Linux, `Cmd` pe macOS. */
function comanda(e: EvenimentTasta): boolean {
  return e.ctrlKey === true || e.metaKey === true;
}

/** Caracterele care pot incepe un numar. O litera NU e printre ele — literele sunt comenzi. */
const INCEPUT_DE_NUMAR = /^[0-9,.\-+]$/;

export function mapeazaTasta(e: EvenimentTasta, ctx: ContextTasta): ActiuneGrila | null {
  const { key } = e;
  const cmd = comanda(e);

  // ---------------------------------------------------------------------
  // In editare: doar iesirea si comiterea ne privesc. Restul apartine inputului,
  // altfel n-am putea tasta o virgula intr-o suma.
  // ---------------------------------------------------------------------
  if (ctx.seEditeaza) {
    if (key === 'Escape') return { tip: 'renunta' };
    if (key === 'Enter') return { tip: 'commit', apoi: e.shiftKey ? 'sus' : 'jos' };
    if (key === 'Tab') return { tip: 'commit', apoi: e.shiftKey ? 'stanga' : 'dreapta' };
    return null;
  }

  // ---------------------------------------------------------------------
  // Navigare in timp — Alt, ca sa nu se bata cu deplasarea in grila
  // ---------------------------------------------------------------------
  if (e.altKey && !cmd) {
    if (key === 'ArrowLeft') return e.shiftKey ? { tip: 'saptamana', delta: -1 } : { tip: 'zi', delta: -1 };
    if (key === 'ArrowRight') return e.shiftKey ? { tip: 'saptamana', delta: 1 } : { tip: 'zi', delta: 1 };
  }

  // ---------------------------------------------------------------------
  // Comenzi cu Ctrl / Cmd
  // ---------------------------------------------------------------------
  if (cmd) {
    switch (key.toLowerCase()) {
      case 'arrowup':
        return { tip: 'muta-la', rand: 'prima', col: 'aceeasi' };
      case 'arrowdown':
        return { tip: 'muta-la', rand: 'ultima', col: 'aceeasi' };
      case 'home':
        return { tip: 'muta-la', rand: 'prima', col: 'prima' };
      case 'end':
        return { tip: 'muta-la', rand: 'ultima', col: 'ultima' };
      case 'a':
        return { tip: 'selecteaza-tot' };
      case 'd':
        return { tip: 'fill-down' };
      case 'c':
        return { tip: 'copiaza' };
      case 'v':
        return { tip: 'lipeste' };
      case 'p':
        // `Ctrl+Shift+P` amana selectia; `Ctrl+P` ramane tiparirea browserului.
        return e.shiftKey ? { tip: 'amana' } : null;
      default:
        return null;
    }
  }

  // ---------------------------------------------------------------------
  // Deplasare
  // ---------------------------------------------------------------------
  switch (key) {
    case 'ArrowUp':
      return { tip: 'muta', dRand: -1, dCol: 0, extinde: e.shiftKey === true };
    case 'ArrowDown':
      return { tip: 'muta', dRand: 1, dCol: 0, extinde: e.shiftKey === true };
    case 'ArrowLeft':
      return { tip: 'muta', dRand: 0, dCol: -1, extinde: e.shiftKey === true };
    case 'ArrowRight':
      return { tip: 'muta', dRand: 0, dCol: 1, extinde: e.shiftKey === true };
    case 'Home':
      return { tip: 'muta-la', rand: 'aceeasi', col: 'prima' };
    case 'End':
      return { tip: 'muta-la', rand: 'aceeasi', col: 'ultima' };
    case 'PageUp':
      return { tip: 'pagina', directie: -1 };
    case 'PageDown':
      return { tip: 'pagina', directie: 1 };
    case 'Tab':
      // Grila e UN SINGUR tab stop: `Tab` iese din ea catre urmatoarea regiune, nu plimba
      // utilizatorul prin 60.000 de celule. Deci nu il interceptam.
      return null;
    case 'Enter':
      return ctx.editabila ? { tip: 'incepe-editare', caracter: null } : null;
    case 'F2':
      return ctx.editabila ? { tip: 'incepe-editare', caracter: null } : null;
    case 'Escape':
      return { tip: 'renunta' };
    case 'Backspace':
    case 'Delete':
      return ctx.editabila ? { tip: 'sterge' } : null;
    case ' ':
      // Ciclarea starii, in ordinea din legenda. `Shift` inapoi.
      return { tip: 'cicleaza-stare', directie: e.shiftKey ? -1 : 1 };
    default:
      break;
  }

  // ---------------------------------------------------------------------
  // Type-to-fill: o cifra (sau `-`, `,`, `.`, `=`) incepe editarea cu acel caracter
  // ---------------------------------------------------------------------
  if (key.length === 1 && ctx.editabila && ctx.numerica) {
    if (key === '=' || INCEPUT_DE_NUMAR.test(key)) {
      return { tip: 'incepe-editare', caracter: key };
    }
  }

  // ---------------------------------------------------------------------
  // Litere: comenzi. Starile au litera lor (A B E T V U) si alias numeric (1..6).
  // ---------------------------------------------------------------------
  if (key.length === 1) {
    const dupaLitera = statusFromShortCode(key);
    if (dupaLitera !== null) return { tip: 'seteaza-stare', cod: dupaLitera };

    const cifra = Number.parseInt(key, 10);
    if (Number.isInteger(cifra)) {
      const dupaCifra = statusFromNumericAlias(cifra);
      if (dupaCifra !== null) return { tip: 'seteaza-stare', cod: dupaCifra };
    }

    switch (key.toLowerCase()) {
      case 'x':
        return { tip: 'comuta-selectie' };
      case 'f':
        return { tip: 'deschide', ce: 'facturi' };
      case 'c':
        return { tip: 'deschide', ce: 'comentariu' };
      case 'l':
        return { tip: 'deschide', ce: 'atasamente' };
      case '\\':
        return { tip: 'deschide', ce: 'drawer' };
      default:
        return null;
    }
  }

  return null;
}

/**
 * Descrierea scurtaturilor, pentru foaia de ajutor (`?`) si pentru banda de legenda.
 * Tinuta aici ca sa nu poata diverge de maparea reala.
 */
export const SCURTATURI: readonly { taste: string; descriere: string }[] = [
  { taste: '↑ ↓ ← →', descriere: 'Mută cursorul' },
  { taste: 'Shift + ↑↓', descriere: 'Extinde selecția' },
  { taste: 'Home / End', descriere: 'Începutul / sfârșitul rândului' },
  { taste: 'Ctrl + Home / End', descriere: 'Începutul / sfârșitul grilei' },
  { taste: 'PageUp / PageDown', descriere: 'Un ecran în sus / în jos' },
  { taste: 'Alt + ← →', descriere: 'Ziua lucrătoare precedentă / următoare' },
  { taste: 'Alt + Shift + ← →', descriere: 'Săptămâna precedentă / următoare' },
  { taste: 'cifră, - , .', descriere: 'Începe editarea cu acel caracter' },
  { taste: '=', descriere: 'Expresie: =2515,67+2501,07' },
  { taste: 'Enter / Shift+Enter', descriere: 'Salvează și coboară / urcă' },
  { taste: 'Tab / Shift+Tab', descriere: 'Salvează și mergi la dreapta / stânga' },
  { taste: 'Esc', descriere: 'Renunță la modificare' },
  { taste: 'Delete', descriere: 'Golește celula' },
  { taste: 'Space', descriere: 'Ciclează starea (Shift = invers)' },
  { taste: 'A B E T V U', descriere: 'Aprobat · Blocat · Eșalonat · în așTeptare · de Verificat · Urgent' },
  { taste: '1 … 6', descriere: 'Aceleași stări, cu cifre' },
  { taste: 'X', descriere: 'Selectează / deselectează rândul' },
  { taste: 'Ctrl + A', descriere: 'Selectează tot ce e filtrat' },
  { taste: 'Ctrl + D', descriere: 'Copiază valoarea de deasupra' },
  { taste: 'Ctrl + C / Ctrl + V', descriere: 'Copiază / lipește (compatibil Excel)' },
  { taste: 'Ctrl + Shift + P', descriere: 'Amână selecția' },
  { taste: 'F / C / L', descriere: 'Facturi · Comentariu · Atașamente' },
  { taste: '\\', descriere: 'Deschide / închide panoul lateral' },
];
